/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  useState, useContext, useEffect, useRef,
} from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import { AuthContext } from '../context/AuthContext';
import business from '../data/business';
import {
  products, KINDLING,
  subscriptionMonthly, clampBundles, bundlesFromPlan,
  SUB_MIN_BUNDLES, SUB_MAX_BUNDLES, SUB_PER_BUNDLE,
  SUBSCRIPTION_WEEKS, subscriptionWeekLabel,
  FIRST_ORDER_MIN_BUNDLES, cartBundleCount, SUBSCRIPTIONS_ENABLED,
} from '../data/pricing';
import MonthCalendar from '../components/MonthCalendar';
import ReferralShare from '../components/ReferralShare';
import LowStockBanner from '../components/LowStockBanner';
import WoodTypeBadge from '../components/WoodTypeBadge';
import neighborhoods from '../data/neighborhoods';
import { todayStr, formatDayLabel, addDays } from '../utils/dates';
import { trackEvent } from '../utils/analytics';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const inputClass = 'block w-full rounded-xl border border-cream-300 bg-white px-4 py-3 text-base text-walnut placeholder:text-walnut-200 transition-colors focus:border-ember focus:outline-none focus:ring-2 focus:ring-ember/30';
const labelClass = 'block text-sm font-semibold text-walnut';

function Order() {
  const { user, token, refreshUser } = useContext(AuthContext);
  const location = useLocation();
  const reorder = location.state?.reorder || null;
  // The chatbot hands off a partial order via location.state.prefill (date/times/contact —
  // the fields the `reorder` shape above doesn't cover). Null on every other entry path.
  const prefill = location.state?.prefill || null;
  const [searchParams] = useSearchParams();

  // Reorder prefill: map past item names back to product ids and pick the mode.
  const initialQty = (() => {
    const q = {};
    (reorder?.items || []).forEach((it) => {
      const p = [...products, KINDLING].find((x) => x.name === it.name);
      if (p) q[p.id] = Number(it.quantity) || 1;
    });
    return q;
  })();

  const [mode, setMode] = useState(reorder?.orderType === 'subscription' ? 'subscription' : 'onetime');
  const [qty, setQty] = useState(initialQty);
  const [subBundles, setSubBundles] = useState(() => {
    if (reorder?.subscriptionBundles) return clampBundles(reorder.subscriptionBundles);
    if (reorder?.subscriptionPlan) return clampBundles(bundlesFromPlan(reorder.subscriptionPlan));
    return SUB_MIN_BUNDLES;
  });
  const [agreedSub, setAgreedSub] = useState(false);
  // Subscription preferred week of the month ('1'..'4' or 'any'); default to the flexible option.
  const [subWeek, setSubWeek] = useState(reorder?.subscriptionWeek || 'any');

  const [preferredDate, setPreferredDate] = useState(prefill?.preferredDate || '');
  const [dateOverrides, setDateOverrides] = useState({});
  const [leadDays, setLeadDays] = useState(1);
  const [rushEnabled, setRushEnabled] = useState(true);
  const [rushPercent, setRushPercent] = useState(25);
  const [rushRequested, setRushRequested] = useState(false);
  const [venmoHandle, setVenmoHandle] = useState('');
  const [cardEnabled, setCardEnabled] = useState(false);
  const [kindling, setKindling] = useState({ enabled: false, price: 0, quantity: 0 });
  const [payMethod, setPayMethod] = useState('venmo'); // 'card' | 'venmo'
  const [returnStatus, setReturnStatus] = useState(''); // 'paid' | 'cancelled' after Stripe redirect
  const [trackToken, setTrackToken] = useState(''); // tracking token of the just-placed order
  const [paymentCheck, setPaymentCheck] = useState('checking'); // 'checking' | 'paid' | 'pending'

  const [codeInput, setCodeInput] = useState('');
  const [appliedCode, setAppliedCode] = useState('');
  const [discountInfo, setDiscountInfo] = useState(null); // { discount, label }
  const [codeError, setCodeError] = useState('');
  // { enabled, type, value } from settings; eligible = signed-in with no prior orders.
  const [firstOrderCfg, setFirstOrderCfg] = useState(null);
  const [firstOrderEligible, setFirstOrderEligible] = useState(false);

  const [contact, setContact] = useState(prefill?.contact || { name: '', phone: '', email: '' });
  const [address, setAddress] = useState(reorder?.deliveryAddress ? {
    street: reorder.deliveryAddress.street || '',
    unit: reorder.deliveryAddress.unit || '',
    neighborhood: reorder.deliveryAddress.neighborhood || '',
    notes: reorder.deliveryAddress.notes || '',
  } : {
    street: '', unit: '', neighborhood: '', notes: '',
  });

  // Logged-in customers can save the entered phone + address to their account for next time.
  const [saveToAccount, setSaveToAccount] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const errorRef = useRef(null);

  const isSubscription = mode === 'subscription';

  // Prefill contact + saved address for logged-in users. Only fills still-empty fields, so a
  // reorder/chatbot value or anything the customer already typed is never clobbered.
  useEffect(() => {
    if (user) {
      setContact((prev) => ({
        name: prev.name || [user.firstName, user.lastName].filter(Boolean).join(' '),
        phone: prev.phone || user.phone || '',
        email: prev.email || user.email || '',
      }));
      if (user.address) {
        setAddress((prev) => ({
          street: prev.street || user.address.street || '',
          unit: prev.unit || user.address.unit || '',
          neighborhood: prev.neighborhood || user.address.neighborhood || '',
          notes: prev.notes || user.address.notes || '',
        }));
      }
    }
  }, [user]);

  // Sold out? (The server enforces this too; this just saves filling in a form for nothing.)
  const [soldOut, setSoldOut] = useState(false);
  useEffect(() => {
    axios.get(`${API_URL}/api/inventory/public`)
      .then((res) => setSoldOut(Boolean(res.data?.soldOut)))
      .catch(() => setSoldOut(false));
  }, []);

  // Load admin availability + scheduling rules.
  useEffect(() => {
    axios.get(`${API_URL}/api/settings/availability`)
      .then((res) => {
        setDateOverrides(res.data.dateOverrides || {});
        if (res.data.leadDays !== undefined) setLeadDays(res.data.leadDays);
        if (res.data.rushEnabled !== undefined) setRushEnabled(res.data.rushEnabled);
        if (res.data.rushPercent !== undefined) setRushPercent(res.data.rushPercent);
        if (res.data.venmoHandle !== undefined) setVenmoHandle(res.data.venmoHandle);
        if (res.data.firstOrderDiscount) setFirstOrderCfg(res.data.firstOrderDiscount);
        if (res.data.cardEnabled) {
          setCardEnabled(true);
          setPayMethod('card');
        }
        if (res.data.kindling) setKindling(res.data.kindling);
      })
      .catch(() => setDateOverrides({}));
  }, []);

  // First-order eligibility (signed-in customers only). The server checks no prior order by this
  // account, phone, or address — so re-check (debounced) as the customer fills in phone/street,
  // keeping the shown discount in sync with what the create route will actually apply.
  useEffect(() => {
    if (!token) { setFirstOrderEligible(false); return undefined; }
    const t = setTimeout(() => {
      axios.get(`${API_URL}/api/promos/first-order`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { phone: contact.phone, street: address.street },
      })
        .then((res) => setFirstOrderEligible(Boolean(res.data.eligible)))
        .catch(() => setFirstOrderEligible(false));
    }, 400);
    return () => clearTimeout(t);
  }, [token, contact.phone, address.street]);

  // Subscriptions require card auto-pay (and are switched off for now); never sit in subscription
  // mode when they aren't offered — e.g. an "order again" link from an old subscription.
  const subsOffered = cardEnabled && SUBSCRIPTIONS_ENABLED;
  useEffect(() => {
    if (!subsOffered && mode === 'subscription') setMode('onetime');
  }, [subsOffered, mode]);

  // --- Cart ---
  // Firewood bundles, plus the Fire Starter Pack add-on when it's enabled AND in stock.
  const displayProducts = kindling.enabled && kindling.quantity > 0
    ? [...products, { ...KINDLING, price: Number(kindling.price) || 0 }]
    : products;
  const setProductQty = (id, n) => setQty((prev) => ({ ...prev, [id]: Math.max(0, n) }));
  const cart = displayProducts
    .filter((p) => (qty[p.id] || 0) > 0)
    .map((p) => ({ ...p, count: qty[p.id] }));
  const itemsSub = cart.reduce((s, c) => s + c.price * c.count, 0);
  const cartBundles = cartBundleCount(cart);
  const subMonthly = subscriptionMonthly(subBundles);

  // --- Date / rush (customers pick a day; we deliver by business.deliverBy) ---
  const today = todayStr();
  const earliest = addDays(today, leadDays);
  // dateOverrides[date] = [] marks a closed day.
  const isClosed = (date) => Array.isArray(dateOverrides[date]) && dateOverrides[date].length === 0;
  const isRushDate = (date) => date >= today && date < earliest;
  const dateIsOpen = (date) => date >= today
    && !isClosed(date)
    && (date >= earliest || (rushEnabled && rushRequested));
  const isRush = Boolean(preferredDate) && isRushDate(preferredDate);

  const getDayState = (date) => {
    if (date < today) return { disabled: true, tone: 'open' };
    const closed = isClosed(date);
    const rushWindow = isRushDate(date);
    return {
      disabled: closed || (rushWindow && !(rushEnabled && rushRequested)),
      // eslint-disable-next-line no-nested-ternary
      tone: closed ? 'closed' : (rushWindow ? 'rush' : 'open'),
      selected: date === preferredDate,
    };
  };

  useEffect(() => {
    if (!rushRequested && preferredDate && isRushDate(preferredDate)) setPreferredDate('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rushRequested, preferredDate, earliest]);

  // --- Totals (one-time orders; subscriptions show the recurring tier price) ---
  const rushSurcharge = isRush ? Math.round(itemsSub * (rushPercent / 100)) : 0;
  const subtotalNum = itemsSub + rushSurcharge; // pre-discount (delivery is free)
  // A promo/referral code wins; otherwise a signed-in first-timer gets the first-order deal.
  const codeDiscount = (!isSubscription && discountInfo?.discount) || 0;
  // First-order deal only applies to a qualifying first order (min bundles) so a single $15
  // bundle can't be fully covered by the $15 deal. Mirrored server-side in routes/orders.js.
  const foQualifies = cartBundles >= FIRST_ORDER_MIN_BUNDLES;
  const foEnabled = !isSubscription && !appliedCode && Boolean(token)
    && firstOrderEligible && firstOrderCfg?.enabled && foQualifies;
  const foRaw = firstOrderCfg && firstOrderCfg.type === 'percent'
    ? Math.round(subtotalNum * ((firstOrderCfg.value || 0) / 100))
    : (firstOrderCfg?.value || 0);
  const firstOrderAmount = foEnabled ? Math.max(0, Math.min(foRaw, subtotalNum)) : 0;
  const discount = codeDiscount || firstOrderAmount;
  const discountRowLabel = appliedCode ? `Promo ${appliedCode}` : 'First-order discount';
  const finalTotalNum = Math.max(0, subtotalNum - discount);

  // --- Promo / referral code ---
  const applyCode = async (codeArg) => {
    setCodeError('');
    const c = (codeArg ?? codeInput).trim();
    if (!c) return;
    try {
      const res = await axios.post(`${API_URL}/api/promos/validate`, { code: c, subtotal: subtotalNum });
      if (res.data.valid) {
        setAppliedCode(res.data.code);
        setDiscountInfo({ discount: res.data.discount, label: res.data.label });
      } else {
        setAppliedCode('');
        setDiscountInfo(null);
        setCodeError(res.data.message || 'That code isn’t valid.');
      }
    } catch {
      setCodeError('Could not check that code. Try again.');
    }
  };

  const removeCode = () => {
    setAppliedCode('');
    setDiscountInfo(null);
    setCodeInput('');
    setCodeError('');
  };

  useEffect(() => {
    if (!appliedCode || isSubscription) return undefined;
    let cancelled = false;
    axios.post(`${API_URL}/api/promos/validate`, { code: appliedCode, subtotal: subtotalNum })
      .then((res) => {
        if (!cancelled && res.data.valid) {
          setDiscountInfo({ discount: res.data.discount, label: res.data.label });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotalNum, appliedCode, isSubscription]);

  // Auto-apply a referral/promo code from a shared link (/order?ref=CODE), once on mount.
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setCodeInput(ref);
      applyCode(ref);
    }
    const status = searchParams.get('status');
    if (status === 'paid' || status === 'cancelled') setReturnStatus(status);
    const track = searchParams.get('track');
    if (track) setTrackToken(track);
    // Don't trust ?status=paid on its own — confirm the payment server-side. Hitting the tracking
    // endpoint also self-heals the order if the Stripe webhook was delayed/misconfigured.
    if (status === 'paid' && track) {
      setPaymentCheck('checking');
      axios.get(`${API_URL}/api/orders/track/${track}`)
        .then((res) => setPaymentCheck(res.data?.paymentStatus === 'paid' ? 'paid' : 'pending'))
        .catch(() => setPaymentCheck('pending'));
    }
    // Reorder deep-link from an email (/order?reorder=<trackingToken>). The in-app "Order again"
    // button seeds these via router state; an email can't, so fetch the past order and apply the
    // same prefill imperatively. Skip if router state already provided a reorder.
    const reorderToken = searchParams.get('reorder');
    if (reorderToken && !reorder) {
      axios.get(`${API_URL}/api/orders/reorder/${reorderToken}`)
        .then(({ data }) => {
          if (data.orderType === 'subscription') {
            setMode('subscription');
            if (data.subscriptionBundles) setSubBundles(clampBundles(data.subscriptionBundles));
            if (data.subscriptionWeek) setSubWeek(data.subscriptionWeek);
          } else {
            setMode('onetime');
            const q = {};
            (data.items || []).forEach((it) => {
              const p = [...products, KINDLING].find((x) => x.name === it.name);
              if (p) q[p.id] = Number(it.quantity) || 1;
            });
            if (Object.keys(q).length) setQty(q);
          }
          if (data.deliveryAddress) {
            setAddress({
              street: data.deliveryAddress.street || '',
              unit: data.deliveryAddress.unit || '',
              neighborhood: data.deliveryAddress.neighborhood || '',
              notes: data.deliveryAddress.notes || '',
            });
          }
        })
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The success / payment-return views render on the same /order route (no path change), so scroll
  // to the top when one appears — otherwise the page stays where the form was scrolled.
  useEffect(() => {
    if (submitted || returnStatus) window.scrollTo(0, 0);
  }, [submitted, returnStatus]);

  // Fire a GA4 conversion when an order completes. Card payments confirm on the Stripe return
  // (purchase); Venmo orders are placed-but-unpaid at submit time (generate_lead). No-op unless
  // analytics is on. The cart is gone after the Stripe redirect, so 'purchase' carries no value.
  useEffect(() => {
    if (returnStatus === 'paid' && paymentCheck === 'paid') {
      trackEvent('purchase', { transaction_id: trackToken || undefined, currency: 'USD' });
    } else if (submitted) {
      trackEvent('generate_lead', {
        currency: 'USD',
        value: isSubscription ? subMonthly : finalTotalNum,
        order_type: mode,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted, returnStatus, paymentCheck]);

  // The error banner sits at the top of the form; scroll it into view so a failed
  // submit (e.g. an empty cart) isn't missed when the button is far below.
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  const buildPayload = () => {
    const base = {
      contact,
      deliveryAddress: address,
      preferredDate,
      rush: isRush,
      code: appliedCode,
      subtotal: subtotalNum,
    };
    if (isSubscription) {
      return {
        ...base,
        orderType: 'subscription',
        subscriptionBundles: subBundles,
        subscriptionWeek: subWeek,
        agreedToTerms: agreedSub,
        paymentMethod: 'card',
        preferredDate: '',
      };
    }
    return {
      ...base,
      orderType: 'onetime',
      items: cart.map((c) => ({ name: c.name, quantity: c.count, unitPrice: c.price })),
      paymentMethod: cardEnabled && payMethod === 'card' ? 'card' : 'venmo',
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isSubscription && cart.length === 0) {
      setError('Please add at least one item.');
      return;
    }
    if (isSubscription && !agreedSub) {
      setError('Please authorize the monthly subscription to continue.');
      return;
    }
    // Subscriptions pick a week of the month (always set); one-time orders need a date.
    if (!isSubscription && (!preferredDate || !dateIsOpen(preferredDate))) {
      setError('Please choose an available date.');
      return;
    }
    if (!address.street.trim()) {
      setError('Please enter a delivery address.');
      return;
    }

    setSubmitting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // Save the entered phone + address back to the account (fire-and-forget so it survives a
      // Stripe redirect and never blocks the order). Done before the order POST for that reason.
      if (token && saveToAccount) {
        axios
          .put(
            `${API_URL}/api/auth/profile`,
            { phone: contact.phone, address },
            { headers },
          )
          .then(() => refreshUser())
          .catch(() => {});
      }
      const res = await axios.post(`${API_URL}/api/orders`, buildPayload(), { headers });
      if (res.data.stripeCheckoutUrl) {
        // Card order — hand off to Stripe Checkout (returns to /order?status=paid|cancelled).
        window.location = res.data.stripeCheckoutUrl;
        return;
      }
      if (res.data.trackingToken) setTrackToken(res.data.trackingToken);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Returned from Stripe Checkout (full reload — cart state is gone; details are in the email).
  // We confirm payment server-side rather than trusting the ?status=paid param.
  if (returnStatus === 'paid') {
    if (paymentCheck === 'checking') {
      return (
        <div className="mx-auto max-w-xl px-4 py-16 text-center sm:px-6">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-cream-300 border-t-ember" aria-hidden="true" />
          <h1 className="mt-6 text-2xl font-extrabold text-walnut">Confirming your payment…</h1>
          <p className="mt-2 text-walnut-400">Just a moment while we confirm your order.</p>
        </div>
      );
    }
    if (paymentCheck === 'pending') {
      return (
        <div className="mx-auto max-w-xl px-4 py-16 text-center sm:px-6">
          <h1 className="mt-4 text-2xl font-extrabold text-walnut">Almost there</h1>
          <p className="mt-2 text-walnut-400">
            We&apos;re confirming your payment — this can take a moment. If you completed checkout,
            your order will update shortly. Follow along below.
          </p>
          {trackToken && (
            <Link to={`/track/${trackToken}`} className="mt-6 inline-block rounded-xl bg-ember px-6 py-3 text-sm font-semibold text-white hover:bg-ember-600">
              Track your order →
            </Link>
          )}
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center sm:px-6">
        <CheckCircleIcon className="mx-auto h-14 w-14 text-green-600" aria-hidden="true" />
        <h1 className="mt-4 text-2xl font-extrabold text-walnut">Payment received — thank you!</h1>
        <p className="mt-2 text-walnut-400">
          Your order is paid and confirmed. We&apos;ll confirm your delivery day shortly — follow
          along below.
        </p>
        {trackToken ? (
          <Link to={`/track/${trackToken}`} className="mt-6 inline-block rounded-xl bg-ember px-6 py-3 text-sm font-semibold text-white hover:bg-ember-600">
            Track your order →
          </Link>
        ) : (
          <Link to="/" className="mt-6 inline-block rounded-xl bg-ember px-6 py-3 text-sm font-semibold text-white hover:bg-ember-600">
            Back home
          </Link>
        )}
      </div>
    );
  }
  if (returnStatus === 'cancelled') {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center sm:px-6">
        <h1 className="text-2xl font-extrabold text-walnut">Payment canceled</h1>
        <p className="mt-2 text-walnut-400">
          No charge was made and your order is held as unpaid. You can try again or pay by Venmo.
        </p>
        <button
          type="button"
          onClick={() => setReturnStatus('')}
          className="mt-6 inline-block rounded-xl bg-ember px-6 py-3 text-sm font-semibold text-white hover:bg-ember-600"
        >
          Start a new order
        </button>
      </div>
    );
  }

  if (submitted) {
    const handle = (venmoHandle || '').replace(/^@/, '');
    const amountNum = isSubscription ? String(subMonthly || '') : String(finalTotalNum);
    const venmoNote = `${business.name} firewood order`;
    const venmoUrl = handle
      ? `https://venmo.com/${handle}?txn=pay${amountNum ? `&amount=${amountNum}` : ''}&note=${encodeURIComponent(venmoNote)}`
      : '';
    const addressLine = [address.street, address.unit, address.neighborhood].filter(Boolean).join(', ');
    const orderLabel = isSubscription
      ? `${subBundles} bundles / month subscription`
      : cart.map((c) => `${c.count}× ${c.name}`).join(', ');
    const summaryRows = [
      ['Order', orderLabel],
      ['When', isSubscription
        ? `${subscriptionWeekLabel(subWeek)} each month`
        : `${formatDayLabel(preferredDate)} · by ${business.deliverBy}`],
      ['How', 'Delivery'],
      ['Address', addressLine],
      ...(isRush ? [['Rush', `Yes (+${rushPercent}%)`]] : []),
      ...(discount > 0 ? [[discountRowLabel, `−$${discount}`]] : []),
      [isSubscription ? 'Price' : 'Estimated total', isSubscription ? `$${subMonthly}/mo` : `$${finalTotalNum}`],
    ].filter(([, v]) => v);

    return (
      <div className="mx-auto max-w-xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center">
          <CheckCircleIcon className="mx-auto h-16 w-16 text-ember" aria-hidden="true" />
          <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-walnut">Order received!</h1>
          <p className="mt-2 text-walnut-400">Thanks! Here are your details — we&apos;ll confirm timing with you shortly.</p>
        </div>

        <div className="mt-8 rounded-2xl border border-cream-300 bg-cream-100 p-6 text-left">
          <dl className="space-y-2 text-sm">
            {summaryRows.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-4">
                <dt className="shrink-0 text-walnut-300">{label}</dt>
                <dd className="text-right font-semibold text-walnut">{value}</dd>
              </div>
            ))}
          </dl>

          <p className="mt-4 rounded-lg bg-white p-3 text-sm text-walnut">
            We’ll do our best to deliver within your preferred time.
          </p>

          {venmoUrl && (
            <a
              href={venmoUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-4 flex w-full items-center justify-center rounded-xl bg-[#3D95CE] px-4 py-3 text-base font-semibold text-white transition-opacity hover:opacity-90"
            >
              {amountNum ? `Pay $${amountNum} with Venmo` : 'Pay with Venmo'}
              {` · @${handle}`}
            </a>
          )}
          <p className="mt-2 rounded-lg bg-amber-50 p-3 text-sm font-semibold text-amber-800">
            Please send your Venmo now — the button above is pre-filled with your total. We deliver
            your order only once payment comes through, so paying right away keeps it on schedule.
          </p>
        </div>

        {trackToken && (
          <Link to={`/track/${trackToken}`} className="mt-6 flex w-full items-center justify-center rounded-xl border border-ember px-4 py-3 text-base font-semibold text-ember hover:bg-ember hover:text-white">
            Track your order →
          </Link>
        )}

        <ReferralShare className="mt-6" />

        {!isSubscription && subsOffered && (
          <div className="mt-6 rounded-xl border border-ember/30 bg-ember/5 p-4 text-center">
            <p className="text-sm text-walnut">
              <span className="font-semibold">Order firewood often?</span>
              {` Subscribe and pay just $${SUB_PER_BUNDLE}/bundle vs $${products.find((p) => p.id === 'standard-bundle')?.price || 15} one-time — cancel anytime.`}
            </p>
            <button
              type="button"
              onClick={() => { setMode('subscription'); setSubmitted(false); window.scrollTo(0, 0); }}
              className="mt-3 rounded-md bg-ember px-4 py-2 text-sm font-semibold text-white hover:bg-ember-600"
            >
              Switch to a subscription &amp; save
            </button>
          </div>
        )}

        <div className="mt-8 flex justify-center gap-4">
          <Link to="/" className="rounded-md bg-cream-300 px-5 py-2.5 text-sm font-semibold text-walnut hover:bg-cream-400">
            Back home
          </Link>
          {token && (
            <Link to="/my-orders" className="rounded-md bg-ember px-5 py-2.5 text-sm font-semibold text-white hover:bg-ember-600">
              View my orders
            </Link>
          )}
        </div>
        <p className="mt-6 text-center text-sm text-walnut-400">
          Questions? Email
          {' '}
          <a href={`mailto:${business.email}`} className="font-semibold text-ember">{business.email}</a>
          .
        </p>
      </div>
    );
  }

  // Subheader payment note, matched to how this order actually pays.
  const payNote = (() => {
    if (isSubscription) return 'Set up secure card billing at checkout — billed monthly, cancel anytime.';
    if (cardEnabled) return 'Pay by card or Venmo at checkout — your choice.';
    return 'Pay with Venmo right after you order.';
  })();
  // Note under the order-total box.
  const totalNote = (() => {
    if (isSubscription) return 'Billed to your card monthly — entered securely at checkout. Cancel anytime.';
    if (cardEnabled && payMethod === 'card') return "You'll pay securely by card on the next step.";
    return "Pay with Venmo right after you submit — we'll pre-fill this amount.";
  })();

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-extrabold tracking-tight text-walnut">Order Firewood</h1>
      <p className="mt-2 text-walnut-400">
        Seasoned, ready-to-burn hardwood bundles in
        {' '}
        {business.serviceArea}
        {`. ${payNote}`}
      </p>

      <LowStockBanner className="mt-4" cta={false} />

      {!token && (
        <p className="mt-4 rounded-md bg-cream-300/60 px-4 py-3 text-sm text-walnut">
          Ordering as a guest.
          {' '}
          <Link to="/login" className="font-semibold text-ember">Sign in</Link>
          {' '}
          to track your orders.
        </p>
      )}

      <form onSubmit={handleSubmit} className="mt-8 space-y-8">
        {error && (
          <div ref={errorRef} role="alert" className="rounded-md bg-red-50 p-4 text-sm text-red-800">{error}</div>
        )}

        {/* One-time vs subscription — only shown when subscriptions are offered */}
        {subsOffered && (
          <div>
            <span className={labelClass}>What would you like?</span>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {[
                { id: 'onetime', label: 'One-time order' },
                { id: 'subscription', label: 'Monthly subscription' },
              ].map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMode(m.id)}
                  className={`rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${
                    mode === m.id
                      ? 'border-ember bg-ember text-white'
                      : 'border-cream-300 bg-white text-walnut hover:border-ember'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* One-time: product cart */}
        {!isSubscription && (
          <div>
            <span className={labelClass}>Add items</span>
            <WoodTypeBadge className="mt-2" />
            <div className="mt-2 space-y-3">
              {displayProducts.map((p) => {
                const count = qty[p.id] || 0;
                return (
                  <div
                    key={p.id}
                    className={`flex items-center justify-between gap-4 rounded-xl border p-4 transition-colors ${
                      count > 0 ? 'border-ember bg-cream-100' : 'border-cream-300 bg-white'
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-walnut">
                        {p.name}
                        {p.addon && <span className="ml-2 rounded-full bg-walnut/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-walnut-400">add-on</span>}
                        <span className="ml-2 font-extrabold text-ember">{`$${p.price}`}</span>
                      </p>
                      <p className="text-xs text-walnut-400">{p.description}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        aria-label={`Remove one ${p.name}`}
                        onClick={() => setProductQty(p.id, count - 1)}
                        disabled={count === 0}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-cream-300 text-walnut hover:border-ember disabled:opacity-40"
                      >
                        <MinusIcon className="h-4 w-4" />
                      </button>
                      <span className="w-6 text-center text-sm font-bold text-walnut">{count}</span>
                      <button
                        type="button"
                        aria-label={`Add one ${p.name}`}
                        onClick={() => setProductQty(p.id, count + 1)}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-cream-300 text-walnut hover:border-ember"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Subscription: bundles-per-month quantity */}
        {isSubscription && (
          <div>
            <span className={labelClass}>How many bundles per month? (delivered)</span>
            <div className="mt-2 flex items-center gap-4 rounded-xl border border-cream-300 bg-white p-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  aria-label="Fewer bundles"
                  onClick={() => setSubBundles((n) => clampBundles(n - 1))}
                  disabled={subBundles <= SUB_MIN_BUNDLES}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-cream-300 text-lg font-bold text-walnut transition-colors hover:border-ember disabled:opacity-40"
                >
                  −
                </button>
                <span className="w-8 text-center text-xl font-extrabold text-walnut" aria-live="polite">{subBundles}</span>
                <button
                  type="button"
                  aria-label="More bundles"
                  onClick={() => setSubBundles((n) => clampBundles(n + 1))}
                  disabled={subBundles >= SUB_MAX_BUNDLES}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-cream-300 text-lg font-bold text-walnut transition-colors hover:border-ember disabled:opacity-40"
                >
                  +
                </button>
              </div>
              <div className="leading-tight">
                <span className="block text-lg font-extrabold text-ember">{`$${subMonthly}/mo`}</span>
                <span className="block text-xs text-walnut-400">
                  {`$${SUB_PER_BUNDLE}/bundle · save vs $${products.find((p) => p.id === 'standard-bundle')?.price} one-time`}
                </span>
              </div>
            </div>
            <p className="mt-1 text-xs text-walnut-300">{`Choose ${SUB_MIN_BUNDLES}–${SUB_MAX_BUNDLES} bundles a month.`}</p>
            <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-xl border border-cream-300 bg-cream-100 p-3">
              <input
                type="checkbox"
                checked={agreedSub}
                onChange={(e) => setAgreedSub(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-cream-300 text-ember focus:ring-ember"
              />
              <span className="text-sm text-walnut">
                {`I authorize $${subMonthly}/mo to my card for ${subBundles} bundles, delivered monthly — cancel anytime.`}
              </span>
            </label>
          </div>
        )}

        {/* Subscription: preferred week of the month (we deliver within that week) */}
        {isSubscription && (
          <div>
            <span className={labelClass}>Which week each month?</span>
            <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {SUBSCRIPTION_WEEKS.map((w) => (
                <button
                  type="button"
                  key={w.value}
                  onClick={() => setSubWeek(w.value)}
                  className={`flex flex-col items-start gap-0.5 rounded-xl border p-3 text-left transition-colors ${
                    subWeek === w.value
                      ? 'border-ember bg-cream-100 ring-2 ring-ember/30'
                      : 'border-cream-300 bg-white hover:border-ember'
                  }`}
                >
                  <span className="text-sm font-bold text-walnut">{w.label}</span>
                  <span className="text-xs text-walnut-400">{w.range}</span>
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-walnut-300">
              We’ll do our best to deliver within the week you choose, and we’ll email you
              updates along the way.
            </p>
          </div>
        )}

        {/* One-time: specific date */}
        {!isSubscription && (
          <div>
            <span className={labelClass}>Delivery date</span>

            {rushEnabled && leadDays > 0 && (
              <label className="mt-2 flex cursor-pointer items-start gap-2 rounded-xl border border-cream-300 bg-cream-100 p-3">
                <input
                  type="checkbox"
                  checked={rushRequested}
                  onChange={(e) => setRushRequested(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-cream-300 text-ember focus:ring-ember"
                />
                <span className="text-sm text-walnut">
                  <span className="font-semibold">In a pinch? Request a rush order.</span>
                  {` Unlocks sooner dates for a ${rushPercent}% rush charge — subject to availability.`}
                </span>
              </label>
            )}

            {leadDays > 0 && (
              <p className="mt-2 text-xs font-semibold text-walnut">
                {`Please order at least ${leadDays} day${leadDays === 1 ? '' : 's'} ahead — the earliest date is ${formatDayLabel(earliest)}.`}
                {rushEnabled ? ' Need it sooner? Request a rush order above.' : ''}
              </p>
            )}
            <div className="mt-2">
              <MonthCalendar getDayState={getDayState} onSelectDate={setPreferredDate} />
            </div>
            <p className="mt-1 text-xs text-walnut-300">
              {preferredDate
                ? `Selected: ${formatDayLabel(preferredDate)}${isRush ? ' · rush order' : ''}. Greyed dates are unavailable.`
                : 'Pick a delivery day. Greyed dates are unavailable.'}
            </p>
            <p className="mt-1 text-xs font-semibold text-walnut">
              {`We deliver by ${business.deliverBy} on the day you choose.`}
            </p>
          </div>
        )}

        {/* Delivery address */}
        <fieldset className="space-y-4">
          <legend className="text-base font-bold text-walnut">Delivery address</legend>
          <div>
            <label htmlFor="street" className={labelClass}>Street address</label>
            <input id="street" type="text" required value={address.street} onChange={(e) => setAddress({ ...address, street: e.target.value })} className={`mt-2 ${inputClass}`} />
          </div>
          <div>
            <label htmlFor="unit" className={labelClass}>Unit / apt (optional)</label>
            <input id="unit" type="text" value={address.unit} onChange={(e) => setAddress({ ...address, unit: e.target.value })} className={`mt-2 ${inputClass}`} />
          </div>
          <div>
            <label htmlFor="neighborhood" className={labelClass}>Neighborhood (optional)</label>
            <select id="neighborhood" value={address.neighborhood} onChange={(e) => setAddress({ ...address, neighborhood: e.target.value })} className={`mt-2 ${inputClass}`}>
              <option value="">Select…</option>
              {neighborhoods.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="notes" className={labelClass}>Delivery notes (optional)</label>
            <textarea id="notes" rows={2} value={address.notes} onChange={(e) => setAddress({ ...address, notes: e.target.value })} className={`mt-2 ${inputClass}`} />
          </div>
        </fieldset>

        {/* Contact */}
        <fieldset className="space-y-4">
          <legend className="text-base font-bold text-walnut">Your contact info</legend>
          <div>
            <label htmlFor="name" className={labelClass}>Name</label>
            <input id="name" type="text" required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className={`mt-2 ${inputClass}`} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="phone" className={labelClass}>Phone</label>
              <input id="phone" type="tel" required value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className={`mt-2 ${inputClass}`} />
            </div>
            <div>
              <label htmlFor="email" className={labelClass}>Email (optional)</label>
              <input id="email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className={`mt-2 ${inputClass}`} />
            </div>
          </div>
        </fieldset>

        {/* Save phone + address to the account for next time (logged-in only). */}
        {token && (
          <label className="flex items-center gap-2 text-sm text-walnut">
            <input
              type="checkbox"
              checked={saveToAccount}
              onChange={(e) => setSaveToAccount(e.target.checked)}
              className="h-4 w-4 rounded border-cream-300 text-ember focus:ring-ember"
            />
            Save this phone &amp; address to my account for next time
          </label>
        )}

        {/* Promo / referral code (one-time only) */}
        {!isSubscription && (
          <div>
            <label htmlFor="promo" className={labelClass}>Promo or referral code (optional)</label>
            <div className="mt-2 flex gap-2">
              <input
                id="promo"
                type="text"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value)}
                placeholder="Enter code"
                className={inputClass}
              />
              {appliedCode ? (
                <button type="button" onClick={removeCode} className="shrink-0 rounded-xl border border-cream-300 px-4 py-3 text-sm font-semibold text-walnut hover:border-ember">
                  Remove
                </button>
              ) : (
                <button type="button" onClick={() => applyCode()} className="shrink-0 rounded-xl bg-walnut px-5 py-3 text-sm font-semibold text-white hover:bg-walnut-400">
                  Apply
                </button>
              )}
            </div>
            {codeError && <p className="mt-1 text-xs text-red-600">{codeError}</p>}
            {appliedCode && discountInfo && (
              <p className="mt-1 text-xs font-semibold text-green-700">{`Applied ${appliedCode} — ${discountInfo.label}.`}</p>
            )}
          </div>
        )}

        {/* Payment method (one-time only; card shown when Stripe is enabled) */}
        {!isSubscription && cardEnabled && (
          <div>
            <span className={labelClass}>How would you like to pay?</span>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setPayMethod('card')}
                className={`rounded-xl border p-4 text-left transition-colors ${payMethod === 'card' ? 'border-ember bg-cream-100 ring-2 ring-ember/30' : 'border-cream-300 bg-white hover:border-ember'}`}
              >
                <span className="block text-sm font-bold text-walnut">Pay by card</span>
                <span className="block text-xs text-walnut-400">Secure checkout now</span>
              </button>
              <button
                type="button"
                onClick={() => setPayMethod('venmo')}
                className={`rounded-xl border p-4 text-left transition-colors ${payMethod === 'venmo' ? 'border-ember bg-cream-100 ring-2 ring-ember/30' : 'border-cream-300 bg-white hover:border-ember'}`}
              >
                <span className="block text-sm font-bold text-walnut">Pay with Venmo</span>
                <span className="block text-xs text-walnut-400">After you order</span>
              </button>
            </div>
          </div>
        )}

        {/* First-order deal nudge for logged-out customers */}
        {!isSubscription && !token && firstOrderCfg?.enabled && (
          <div className="rounded-xl border border-ember/30 bg-ember/5 p-4 text-sm text-walnut">
            <span className="font-semibold text-ember">New here?</span>
            {' '}
            {firstOrderCfg.type === 'percent'
              ? `Sign in to claim ${firstOrderCfg.value}% off your first order of ${FIRST_ORDER_MIN_BUNDLES}+ bundles.`
              : `Sign in to claim $${firstOrderCfg.value} off your first order of ${FIRST_ORDER_MIN_BUNDLES}+ bundles.`}
            {' '}
            <Link to="/register" className="font-semibold text-ember underline">Create an account →</Link>
          </div>
        )}

        {/* First-order deal: signed-in & eligible, but the cart isn't big enough yet */}
        {!isSubscription && token && firstOrderEligible && firstOrderCfg?.enabled
          && !appliedCode && cart.length > 0 && !foQualifies && (
          <div className="rounded-xl border border-ember/30 bg-ember/5 p-4 text-sm text-walnut">
            <span className="font-semibold text-ember">Almost there!</span>
            {' '}
            {`Add ${FIRST_ORDER_MIN_BUNDLES - cartBundles} more bundle${FIRST_ORDER_MIN_BUNDLES - cartBundles === 1 ? '' : 's'} `}
            {firstOrderCfg.type === 'percent'
              ? `to get ${firstOrderCfg.value}% off your first order.`
              : `to get $${firstOrderCfg.value} off your first order.`}
          </div>
        )}

        {/* Estimate */}
        <div className="rounded-xl border border-cream-300 bg-cream-100 p-4">
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm font-semibold text-walnut">
              {isSubscription ? 'Monthly price' : 'Estimated total'}
            </span>
            <span className="text-2xl font-extrabold text-ember">
              {isSubscription ? `$${subMonthly}/mo` : `$${finalTotalNum}`}
            </span>
          </div>
          {!isSubscription && (
            <div className="mt-2 space-y-0.5 text-sm text-walnut-400">
              {cart.map((c) => (
                <p key={c.id} className="flex justify-between">
                  <span>{`${c.count}× ${c.name}`}</span>
                  <span>{`$${c.price * c.count}`}</span>
                </p>
              ))}
              {cart.length === 0 && <p>Add items above to see your total.</p>}
              {rushSurcharge > 0 && (
                <p className="flex justify-between font-semibold text-amber-700">
                  <span>{`Rush +${rushPercent}%`}</span>
                  <span>{`$${rushSurcharge}`}</span>
                </p>
              )}
              {discount > 0 && (
                <p className="flex justify-between font-semibold text-green-700">
                  <span>{discountRowLabel}</span>
                  <span>{`−$${discount}`}</span>
                </p>
              )}
            </div>
          )}
          <p className="mt-2 text-xs text-walnut-300">{totalNote}</p>
        </div>

        {soldOut && !isSubscription && (
          <p className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-900">
            We&apos;re sold out right now — a fresh batch is on the way. Check back soon!
          </p>
        )}
        <button
          type="submit"
          disabled={submitting || (soldOut && !isSubscription)}
          className="w-full rounded-xl bg-ember px-4 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-ember-600 disabled:opacity-50"
        >
          {/* eslint-disable-next-line no-nested-ternary */}
          {submitting
            ? 'Submitting…'
            : ((isSubscription || (cardEnabled && payMethod === 'card')) ? 'Continue to payment →' : 'Submit Order')}
        </button>
      </form>
    </div>
  );
}

export default Order;
